import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faArrowLeft,
  faSpinner,
  faStar,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import './GradeLead.css';

const GradeLead = () => {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);
  const [grade, setGrade] = useState(null);
  const [comment, setComment] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentFeedback, setCommentFeedback] = useState(null);
  const [commentError, setCommentError] = useState(null);
  const [isGraded, setIsGraded] = useState(false);
  const [existingGrade, setExistingGrade] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const params = new URLSearchParams(location.search);
  const leadId = params.get('lead_id') || '';
  const token = params.get('token') || '';
  const address = params.get('address') || '';
  const phone = params.get('phone') || '';
  const email = params.get('email') || '';
  const propertyType = params.get('propertyType') || '';
  const numRooms = params.get('numRooms') || '';
  const area = params.get('area') || '';
  const estimatedValue = params.get('estimatedValue') || '';

  useEffect(() => {
    const fetchLeadData = async () => {
      if (!leadId || !token) {
        setStatusLoading(false);
        return;
      }

      try {
        setCommentError(null);

        const [gradeStatusResponse, commentResponse] = await Promise.all([
          axios.get(`${config.backend_url}/api/leads/grade-status`, {
            params: { lead_id: leadId, token }
          }),
          axios.get(`${config.backend_url}/api/leads/comment`, {
            params: { lead_id: leadId, token }
          })
        ]);

        if (gradeStatusResponse.data.success) {
          setIsGraded(gradeStatusResponse.data.is_graded);
          setExistingGrade(gradeStatusResponse.data.grade);
        }

        if (commentResponse.data.success) {
          const fetchedComment = commentResponse.data.comment || '';
          setComment(fetchedComment);
          setSavedComment(fetchedComment);
        } else if (commentResponse.data.error) {
          setCommentError(commentResponse.data.error);
        }
      } catch (err) {
        setCommentError(err.response?.data?.error || 'Kunde inte hämta lead-information.');
      } finally {
        setStatusLoading(false);
      }
    };

    fetchLeadData();
  }, [leadId, token]);

  const handleSubmitGrade = async () => {
    if (!leadId || !token) {
      setError('Saknade parametrar för betygsättning.');
      return;
    }

    if (!grade) {
      setError(comment.trim() ? 'Välj ett betyg innan du sparar din kommentar.' : 'Välj ett betyg innan du fortsätter.');
      return;
    }

    setLoading(true);
    setError(null);
    setFeedback(null);
    setCommentError(null);
    setCommentFeedback(null);

    try {
      const response = await axios.post(`${config.backend_url}/api/leads/grade-decision`, {
        lead_id: leadId,
        token,
        grade,
        comment
      });

      if (response.data.success) {
        const details = response.data.details || {};
        const conversionSent = Boolean(details.conversion_sent);
        const feedbackType = conversionSent ? 'success' : 'warning';
        setFeedback({
          type: feedbackType,
          message: response.data.message || 'Tack för ditt betyg. Din information är skickad till Bodex för bearbetning.',
          details
        });
      } else {
        setError(response.data.error || 'Ett fel uppstod vid betygsättning.');
      }
    } catch (err) {
      console.error('Grading error:', err);
      setError(err.response?.data?.error || 'Ett fel uppstod vid betygsättning. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.push('/');
  };

  const handleGoBackToGradedLead = () => {
    history.push(`/grade-lead${location.search}`);
  };

  const handleSaveComment = async () => {
    if (!leadId || !token) {
      setCommentError('Saknade parametrar för kommentar.');
      return;
    }

    setCommentLoading(true);
    setCommentError(null);
    setCommentFeedback(null);

    try {
      const response = await axios.post(`${config.backend_url}/api/leads/comment`, {
        lead_id: leadId,
        token,
        comment
      });

      if (response.data.success) {
        const newSavedComment = response.data.comment || '';
        setComment(newSavedComment);
        setSavedComment(newSavedComment);
        setCommentFeedback(response.data.message || 'Kommentaren är sparad.');
      } else {
        setCommentError(response.data.error || 'Ett fel uppstod vid sparande.');
      }
    } catch (err) {
      setCommentError(err.response?.data?.error || 'Ett fel uppstod vid sparande. Försök igen.');
    } finally {
      setCommentLoading(false);
    }
  };

  const renderLeadInfo = () => (
    <div className="grade-lead-info">
      <h3>Lead Information</h3>
      <table className="grade-lead-table">
        <tbody>
          <tr>
            <td><strong>Adress:</strong></td>
            <td>{address}</td>
          </tr>
          <tr>
            <td><strong>Telefon:</strong></td>
            <td>{phone}</td>
          </tr>
          <tr>
            <td><strong>E-post:</strong></td>
            <td>{email}</td>
          </tr>
          {propertyType && (
            <tr>
              <td><strong>Typ:</strong></td>
              <td>{propertyType}</td>
            </tr>
          )}
          {numRooms && (
            <tr>
              <td><strong>Antal rum:</strong></td>
              <td>{numRooms}</td>
            </tr>
          )}
          {area && (
            <tr>
              <td><strong>Boarea:</strong></td>
              <td>{area} m²</td>
            </tr>
          )}
          {estimatedValue && (
            <tr>
              <td><strong>Värdering:</strong></td>
              <td>{estimatedValue}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderCommentForm = ({ showSaveButton = true } = {}) => {
    const hasSavedComment = savedComment && savedComment.trim();

    return (
      <div className="grade-lead-comment">
        <div className="comment-header">
          <h3>Frivillig kommentar</h3>
          <p>
            {showSaveButton
              ? 'Lägg till en kort notering. Det går bra att ändra och spara igen.'
              : 'Lägg till en kort notering. Kommentaren sparas tillsammans med ditt betyg.'}
          </p>
        </div>

        {hasSavedComment && (
          <div className="comment-display">
            <div className="comment-display-label">Sparad kommentar:</div>
            <div className="comment-display-text">{savedComment}</div>
          </div>
        )}

        <textarea
          className="comment-textarea"
          placeholder="Skriv din kommentar här..."
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          disabled={commentLoading || loading}
          rows={3}
        />
        {commentError && (
          <div className="comment-error">
            <FontAwesomeIcon icon={faTimesCircle} /> {commentError}
          </div>
        )}
        {commentFeedback && (
          <div className="comment-feedback">
            <FontAwesomeIcon icon={faCheckCircle} /> {commentFeedback}
          </div>
        )}
        {showSaveButton && (
          <div className="comment-actions">
            <button
              className="comment-save-button"
              onClick={handleSaveComment}
              disabled={commentLoading}
            >
              {commentLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Sparar...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} /> Spara kommentar
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (statusLoading) {
    return (
      <div className="grade-lead-container">
        <div className="grade-lead-content">
          <div className="grade-lead-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Laddar lead-information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (feedback) {
    return (
      <div className="grade-lead-container">
        <div className="grade-lead-feedback">
          <div className={`feedback-box ${feedback.type}`}>
            <FontAwesomeIcon
              icon={feedback.type === 'success' ? faCheckCircle : feedback.type === 'warning' ? faExclamationTriangle : faTimesCircle}
              className="feedback-icon"
            />
            <h2>
              {feedback.type === 'success'
                ? 'Betyg mottaget'
                : feedback.type === 'warning'
                  ? 'Betyg mottaget (varning)'
                  : 'Betygsättning misslyckades'}
            </h2>
            <p>{feedback.message}</p>

            {feedback.details.grade && (
              <div className="feedback-details">
                <p><strong>Ditt betyg:</strong> {feedback.details.grade}</p>
                {feedback.details.conversion_sent && (
                  <p><strong>Information skickad till Bodex:</strong> Skickad</p>
                )}
                {!feedback.details.conversion_sent && (
                  <p><strong>Information skickad till Bodex:</strong> Betyget är sparat</p>
                )}
                {feedback.details.conversion_error && (
                  <p><strong>Fel:</strong> {feedback.details.conversion_error}</p>
                )}
              </div>
            )}

            <button className="back-button-feedback" onClick={handleGoBackToGradedLead}>
              <FontAwesomeIcon icon={faArrowLeft} /> Tillbaka till leadet
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isGraded) {
    return (
      <div className="grade-lead-container">
        <div className="grade-lead-content">
          <div className="grade-lead-header">
            <h1>Bodex Lead Betyg</h1>
          </div>

          {renderLeadInfo()}

          <div className="grade-lead-already-graded">
            <FontAwesomeIcon icon={faCheckCircle} className="already-graded-icon" />
            <h2>Leadet är redan markerat. Tack för ditt betyg!</h2>
            {existingGrade && (
              <p className="existing-grade-display">Betyg: {existingGrade}</p>
            )}
          </div>

          {renderCommentForm()}

          <button className="back-button-feedback" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Tillbaka till startsidan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grade-lead-container">
      <div className="grade-lead-content">
        <div className="grade-lead-header">
          <h1>Bodex Lead Betyg</h1>
          <p className="grade-lead-subtitle">Hur varmt är leadet?</p>
          <div className="grade-lead-guidance">
            <span>Skala 1-5 där 1 är kallast och 5 är varmast.</span>
            <span>Du kan bara gradera leadet en gång.</span>
          </div>
        </div>

        <div className="grade-lead-top-grid">
          <div className="grade-lead-question">
            <p className="question-title">
              <FontAwesomeIcon icon={faStar} /> Hur varmt är leadet?
            </p>
            <div className="grade-options">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`grade-option ${grade === value ? 'selected' : ''}`}
                  onClick={() => setGrade(value)}
                  disabled={loading}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="grade-lead-error">
            <FontAwesomeIcon icon={faTimesCircle} /> {error}
          </div>
        )}

        <div className="grade-lead-actions">
          <button
            className="grade-submit-button"
            onClick={handleSubmitGrade}
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Skickar...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} /> Spara
              </>
            )}
          </button>
        </div>

        {renderLeadInfo()}

        <div className="grade-lead-note">
          <p>
            <strong>Obs:</strong> Var försiktig när du sätter betyg och säkerställ att du har gjort en välgrundad bedömning innan du skickar.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GradeLead;
